/** Script iniettato da `PublicTemplate` per il widget embed Iubenda (home, landing, blog pubblico). */
export const IUBENDA_EMBED_SCRIPT_ID = 'iubenda-embed-consent-widget';

export const IUBENDA_EMBED_SCRIPT_SRC =
  'https://embeds.iubenda.com/widgets/fc1f4dc7-3c37-4509-be7c-a860bf4c5f95.js';

/** Frammento univoco dell’URL del widget (evita lasciare loader duplicati). */
const IUBENDA_WIDGET_SRC_MARKER = 'fc1f4dc7-3c37-4509-be7c-a860bf4c5f95';

const IUBENDA_PRIVATE_GUARD_STYLE_ID = 'iubenda-private-guard-style';

/**
 * UI Iubenda Cookie Solution: banner, pulsante preferenze (chiave), overlay.
 * Esclude i link policy nel testo (class tipo `iubenda-noiframe` nel modal): non usiamo `[class*="iubenda-"]` generico.
 * Rif. doc / community: `.iubenda-tp-btn` = floating preferences button.
 */
const IUBENDA_UI_PURGE_SELECTORS: readonly string[] = [
  '#iubenda-cs-banner',
  '[id^="iubenda-cs"]',
  '[id^="iubenda-embed"]',
  '.iubenda-tp-btn',
  '[class*="iubenda-tp-"]',
  '[class*="_iub_cs"]',
  'iframe[src*="cs.iubenda.com"]',
  'iframe[src*="cdn.iubenda.com/cs"]',
];

const IUBENDA_PRIVATE_BLOCKED_SELECTORS = IUBENDA_UI_PURGE_SELECTORS.join(', ');

/** Stato globale creato dall’embed: se resta attivo può reiniettare il banner in SPA. */
function resetIubendaCookieSolutionGlobals(): void {
  const w = window as unknown as Record<string, unknown>;
  for (const key of ['_iub', 'cmp_iub_vendors_purposes'] as const) {
    try {
      delete w[key];
    } catch {
      /* ignore */
    }
  }
}

/** Rimuove nodi Iubenda anche dentro shadow root (il tasto preferenze spesso non sta nel light DOM). */
function purgeIubendaUiInSubtree(root: ParentNode): void {
  root.querySelectorAll('*').forEach((host) => {
    if (host.shadowRoot) {
      purgeIubendaUiInSubtree(host.shadowRoot);
    }
  });
  for (const sel of IUBENDA_UI_PURGE_SELECTORS) {
    root.querySelectorAll(sel).forEach((n) => {
      n.remove();
    });
  }
}

/**
 * Rimuove loader + UI del widget consenso Iubenda (embed pubblico) e script CS correlati.
 * Non rimuove `https://cdn.iubenda.com/iubenda.js` usato dal modal privacy policy.
 */
export function teardownIubendaPublicConsentWidget(): void {
  const scriptSelector = [
    `script#${IUBENDA_EMBED_SCRIPT_ID}`,
    `script[src*="${IUBENDA_WIDGET_SRC_MARKER}"]`,
    'script[src*="cdn.iubenda.com/cs/"]',
    'script[src*="cs.iubenda.com"]',
  ].join(', ');

  document.querySelectorAll<HTMLScriptElement>(scriptSelector).forEach((s) => {
    s.remove();
  });

  document
    .querySelectorAll<HTMLLinkElement>(
      'link[rel="stylesheet"][href*="cdn.iubenda.com/cs/"], link[rel="stylesheet"][href*="cs.iubenda.com"]',
    )
    .forEach((l) => {
      l.remove();
    });

  purgeIubendaUiInSubtree(document.documentElement);

  resetIubendaCookieSolutionGlobals();
}

/**
 * Nell'area privata impedisce al widget pubblico di comparire anche se
 * lo script pubblico completa l'iniezione in ritardo dopo il cambio route SPA.
 */
export function mountIubendaPrivateGuard(): () => void {
  teardownIubendaPublicConsentWidget();

  let style = document.getElementById(
    IUBENDA_PRIVATE_GUARD_STYLE_ID,
  ) as HTMLStyleElement | null;

  if (!style) {
    style = document.createElement('style');
    style.id = IUBENDA_PRIVATE_GUARD_STYLE_ID;
    style.textContent = `
      ${IUBENDA_PRIVATE_BLOCKED_SELECTORS} {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  let raf = 0;
  const scheduleTeardown = () => {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      teardownIubendaPublicConsentWidget();
    });
  };

  const observer = new MutationObserver(scheduleTeardown);
  const target = document.body || document.documentElement;
  observer.observe(target, { childList: true, subtree: true });

  // Copre la corsa embed async: qualche tick dopo il login il DOM compare comunque.
  const BURST_MS = 4000;
  const BURST_STEP_MS = 150;
  let burstElapsed = 0;
  const burstId = window.setInterval(() => {
    teardownIubendaPublicConsentWidget();
    burstElapsed += BURST_STEP_MS;
    if (burstElapsed >= BURST_MS) {
      window.clearInterval(burstId);
    }
  }, BURST_STEP_MS);

  return () => {
    window.clearInterval(burstId);
    observer.disconnect();
    document.getElementById(IUBENDA_PRIVATE_GUARD_STYLE_ID)?.remove();
    teardownIubendaPublicConsentWidget();
  };
}
